import * as Sentry from '@sentry/react'
import type { TFunction } from 'i18next'

import { getSentryFeedbackIntegrationOptions } from '@/i18n/sentryFeedbackStrings'

interface SentryConfig {
	dsn: string
	environment: string
	release?: string
	tracesSampleRate: number
	replaysSessionSampleRate: number
	replaysOnErrorSampleRate: number
	enableSessionReplay: boolean
	sendDefaultPii: boolean
	enableUserFeedback: boolean
	enableCrashReportModal: boolean
	networkDetailAllowUrls: (string | RegExp)[]
}

/**
 * Map the runtime SENTRY_SEND_PII flag onto Sentry 11 dataCollection.
 * When PII is off, automatic user info, cookies, headers, bodies, and query
 * params stay off. Stack frames stay at the SDK default.
 */
function sentryDataCollection(sendPii: boolean): {
	userInfo: boolean
	cookies: boolean
	httpHeaders: boolean
	httpBodies: Array<'incomingRequest' | 'outgoingRequest' | 'incomingResponse' | 'outgoingResponse'>
	urlQueryParams: boolean
	graphQL: { document: boolean; variables: boolean }
	genAI: { inputs: boolean; outputs: boolean }
	databaseQueryData: boolean
	queues: boolean
} {
	return {
		userInfo: sendPii,
		cookies: sendPii,
		httpHeaders: sendPii,
		httpBodies: sendPii
			? ['incomingRequest', 'outgoingRequest', 'incomingResponse', 'outgoingResponse']
			: [],
		urlQueryParams: sendPii,
		graphQL: { document: sendPii, variables: sendPii },
		genAI: { inputs: sendPii, outputs: sendPii },
		databaseQueryData: sendPii,
		queues: sendPii,
	}
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 * @param t - i18n translate function (Sentry feedback UI must match app language)
 * @returns true if Sentry was initialized, false if disabled
 */
export const initSentry = (t: TFunction): boolean => {
	// Get configuration from runtime config (injected at container runtime)
	const config = getSentryConfig()
	const feedbackStrings = getSentryFeedbackIntegrationOptions(t)

	// If no DSN is provided, Sentry is disabled
	if (!config.dsn) {
		console.log('Sentry is disabled (no DSN provided)')
		return false
	}

	// Feedback form state – hoisted so they persist across onFormOpen/onFormClose cycles
	let dialogWrapperObserver: MutationObserver | null = null
	let toolObserver: MutationObserver | null = null
	let tooltipInterval: ReturnType<typeof setInterval> | null = null
	let hasRun = false

	const cleanupFeedbackForm = () => {
		if (dialogWrapperObserver) {
			dialogWrapperObserver.disconnect()
			dialogWrapperObserver = null
		}
		if (toolObserver) {
			toolObserver.disconnect()
			toolObserver = null
		}
		if (tooltipInterval) {
			clearInterval(tooltipInterval)
			tooltipInterval = null
		}
	}

	try {
		Sentry.init({
			dsn: config.dsn,
			environment: config.environment,
			release: config.release,

			// See in console whether events are sent or dropped (development)
			debug: config.environment === 'development',

			// PII settings. Sentry 11 replaced sendDefaultPii with per-category dataCollection.
			// SENTRY_SEND_PII still toggles the categories the old flag used to enable.
			dataCollection: sentryDataCollection(config.sendDefaultPii),

			// Performance Monitoring
			tracesSampleRate: config.tracesSampleRate,

			// Session Replay - configurable via flag
			replaysSessionSampleRate: config.enableSessionReplay ? config.replaysSessionSampleRate : 0,
			replaysOnErrorSampleRate: config.enableSessionReplay ? config.replaysOnErrorSampleRate : 0,

			integrations: [
				// Browser tracing for performance monitoring
				Sentry.browserTracingIntegration(),

				// Session Replay integration (only enabled if flag is set)
				...(config.enableSessionReplay
					? [
							Sentry.replayIntegration({
								maskAllText: true,
								blockAllMedia: true,
								networkDetailAllowUrls: config.networkDetailAllowUrls,
							}),
						]
					: []),

				// User Feedback widget (only when enabled)
				...(config.enableUserFeedback
					? [
							Sentry.feedbackIntegration({
								...feedbackStrings,
								autoInject: false,
								showBranding: false,
								colorScheme: 'system',
								themeLight: { accentBackground: '#8c52ff' },
								themeDark: { accentBackground: '#8c52ff' },
								onFormOpen() {
									// Clean up any orphaned resources from a previous open
									cleanupFeedbackForm()
									hasRun = false

									const container = document.getElementById('sentry-feedback')
									const root = container?.shadowRoot ?? container

									const run = () => {
										if (!root || hasRun) return
										hasRun = true

										const sendPii = isSentrySendPiiEnabled()
										const sentryUser = Sentry.getIsolationScope().getUser()

										const nameInput = root.querySelector?.('input[name="name"]') as HTMLInputElement | null
										const emailInput = root.querySelector?.('input[name="email"]') as HTMLInputElement | null

										if (sendPii && sentryUser) {
											// Automatically set name and email from Sentry user when PII is enabled
											if (nameInput && sentryUser.username) nameInput.value = sentryUser.username
											if (emailInput && sentryUser.email) emailInput.value = sentryUser.email
											if (nameInput) nameInput.readOnly = true
											if (emailInput) emailInput.readOnly = true
										}

										// Prevent closing the modal by clicking outside (backdrop)
										const dialogEl = root.querySelector?.('.dialog')
										if (dialogEl && !dialogEl.hasAttribute('data-click-handler-attached')) {
											dialogEl.setAttribute('data-click-handler-attached', 'true')
											dialogEl.addEventListener(
												'click',
												(e) => {
													const target = e.target as Element
													if (target === dialogEl || target?.classList?.contains('dialog__position')) {
														e.stopImmediatePropagation()
														e.preventDefault()
													}
												},
												true
											)
										}

										// Hide the outer container during screenshot capture.
										// The SDK sets dialog.el.style.display = 'none' before getDisplayMedia,
										// but that only hides an inner shadow DOM div. We mirror the change onto
										// the outer #sentry-feedback element so screen capture truly omits it.
										const dialogWrapper = root.querySelector?.(
											':scope > div:last-of-type'
										) as HTMLElement | null
										if (dialogWrapper && container && !dialogWrapperObserver) {
											dialogWrapperObserver = new MutationObserver(() => {
												if (dialogWrapper.style.display === 'none') {
													container.style.display = 'none'
												} else {
													container.style.display = ''
												}
											})
											dialogWrapperObserver.observe(dialogWrapper, {
												attributes: true,
												attributeFilter: ['style'],
											})
										}

										// Add a visible help text below the screenshot editor toolbar.
										// The editor mounts lazily when "Add a screenshot" is clicked,
										// so we poll via MutationObserver + interval until found.
										const addToolHelp = () => {
											const toolContainer = root.querySelector?.(
												'.editor__tool-container'
											) as HTMLElement | null
											if (!toolContainer || toolContainer.querySelector('#sentry-tool-help')) return
											const help = document.createElement('p')
											help.id = 'sentry-tool-help'
											help.setAttribute(
												'style',
												[
													'margin: 6px 20px 0',
													'padding: 0',
													'font-size: 0.8em',
													'line-height: 1.4',
													'color: var(--foreground)',
													'opacity: 0.7',
													'text-align: center',
												].join(';')
											)
											help.textContent = t('sentryFeedback.screenshotEditorHelp')
											toolContainer.appendChild(help)
										}

										if (!toolObserver) {
											toolObserver = new MutationObserver(() => addToolHelp())
											toolObserver.observe(root as Node, { childList: true, subtree: true })
										}

										if (!tooltipInterval) {
											tooltipInterval = setInterval(() => addToolHelp(), 500)
											// Stop polling after 60s to avoid a permanent timer
											setTimeout(() => {
												if (tooltipInterval) {
													clearInterval(tooltipInterval)
													tooltipInterval = null
												}
											}, 60000)
										}

										// Inject a verification checkbox above the submit button.
										// Users must confirm they have reviewed their report for
										// sensitive information before submitting.
										const btnGroup = root.querySelector?.('.btn-group') as HTMLElement | null
										const submitBtn = root.querySelector?.('.btn.btn--primary') as HTMLButtonElement | null
										if (btnGroup && submitBtn && !root.querySelector?.('#sentry-verify-checkbox')) {
											// Disable submit until verified
											submitBtn.disabled = true

											const wrapper = document.createElement('label')
											wrapper.setAttribute(
												'style',
												[
													'display: flex',
													'align-items: flex-start',
													'gap: 8px',
													'margin: 0',
													'padding: 0',
													'cursor: pointer',
													'font-size: 0.85em',
													'line-height: 1.4',
													'color: var(--foreground)',
													'user-select: none',
												].join(';')
											)

											const checkbox = document.createElement('input')
											checkbox.type = 'checkbox'
											checkbox.id = 'sentry-verify-checkbox'
											checkbox.setAttribute(
												'style',
												[
													'margin-top: 2px',
													'flex-shrink: 0',
													'accent-color: #8c52ff',
													'width: 16px',
													'height: 16px',
													'cursor: pointer',
												].join(';')
											)

											const text = document.createElement('span')
											text.textContent = t('sentryFeedback.verifyReportCheckbox')

											checkbox.addEventListener('change', () => {
												submitBtn.disabled = !checkbox.checked
											})

											wrapper.appendChild(checkbox)
											wrapper.appendChild(text)
											btnGroup.parentNode?.insertBefore(wrapper, btnGroup)
										}
									}

									// Run once synchronously, then once more async to catch lazy-loaded elements
									run()
									setTimeout(run, 0)

									// Clean up observers and intervals when form closes
									return () => {
										cleanupFeedbackForm()
										hasRun = false
									}
								},
							}),
						]
					: []),
			],

			// Filter out unnecessary noise
			ignoreErrors: [
				// Browser extensions
				'top.GLOBALS',
				// Random network errors
				'NetworkError',
				'Network request failed',
				// Ignore specific known issues
				'ResizeObserver loop limit exceeded',
				// Expected auth/session errors (old refresh tokens, removed accounts)
				'Session expired or invalid',
				'session_invalidated',
				'Could not validate credentials',
				'Unauthorized',
			],

			beforeSend(event, hint) {
				// Filter out certain errors before sending to Sentry
				const error = hint.originalException

				// Don't send network errors for known endpoints
				if (
					error instanceof Error &&
					(error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))
				) {
					return null
				}

				// Don't send expected auth/session errors (old refresh tokens, removed accounts)
				const message = error instanceof Error ? error.message : String(error ?? '')
				const authPatterns = [
					'Session expired or invalid',
					'session_invalidated',
					'Could not validate credentials',
					'Unauthorized',
					'401',
				]
				if (authPatterns.some((p) => message.includes(p))) {
					return null
				}

				// Show crash-report modal when an exception is sent (if enabled)
				if (config.enableCrashReportModal && event.exception && event.event_id) {
					Sentry.showReportDialog({ eventId: event.event_id })
				}

				return event
			},
		})

		console.log(
			`Sentry initialized for environment: ${config.environment}, session replay: ${config.enableSessionReplay ? 'enabled' : 'disabled'}, user feedback: ${config.enableUserFeedback ? 'enabled' : 'disabled'}, crash-report modal: ${config.enableCrashReportModal ? 'enabled' : 'disabled'}`
		)
		return true
	} catch (error) {
		console.error('Failed to initialize Sentry:', error)
		return false
	}
}

/**
 * Parse SENTRY_NETWORK_DETAIL_ALLOW_URLS (comma-separated) into array for replayIntegration.
 * Per Sentry docs: strings match any URL containing that string.
 */
function parseNetworkDetailAllowUrls(value: string): (string | RegExp)[] {
	if (!value?.trim()) return []
	return value
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean)
}

/**
 * Get Sentry configuration from runtime environment variables
 */
function getSentryConfig(): SentryConfig {
	const runtimeConfig = window.RUNTIME_CONFIG || {}

	return {
		dsn: runtimeConfig.SENTRY_DSN || '',
		environment: runtimeConfig.SENTRY_ENVIRONMENT || 'development',
		release: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
		tracesSampleRate: parseFloat(runtimeConfig.SENTRY_TRACES_SAMPLE_RATE || '0.1'),
		replaysSessionSampleRate: parseFloat(runtimeConfig.SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'),
		replaysOnErrorSampleRate: parseFloat(runtimeConfig.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'),
		enableSessionReplay: runtimeConfig.SENTRY_ENABLE_SESSION_REPLAY?.toLowerCase() === 'true',
		sendDefaultPii: runtimeConfig.SENTRY_SEND_PII?.toLowerCase() === 'true',
		enableUserFeedback: runtimeConfig.SENTRY_ENABLE_USER_FEEDBACK?.toLowerCase() !== 'false',
		enableCrashReportModal: runtimeConfig.SENTRY_ENABLE_CRASH_REPORT_MODAL?.toLowerCase() === 'true',
		networkDetailAllowUrls: parseNetworkDetailAllowUrls(
			runtimeConfig.SENTRY_NETWORK_DETAIL_ALLOW_URLS ?? ''
		),
	}
}

/**
 * Whether Sentry UI components may render without a DSN in env.js (local/dev only).
 */
export function isSentryComponentsEnabled(): boolean {
	return window.RUNTIME_CONFIG?.SENTRY_ENABLE_COMPONENTS?.toLowerCase() === 'true'
}

/**
 * Check if Sentry is enabled (DSN is set, or components forced on for UI dev).
 */
export const isSentryEnabled = (): boolean => {
	const runtimeConfig = window.RUNTIME_CONFIG || {}
	return Boolean(runtimeConfig.SENTRY_DSN) || isSentryComponentsEnabled()
}

/**
 * Whether the in-app Sentry user feedback form can be shown (feedback not disabled via runtime config,
 * and either DSN is set or SENTRY_ENABLE_COMPONENTS forces UI on).
 */
export function isSentryUserFeedbackAvailable(): boolean {
	const runtimeConfig = window.RUNTIME_CONFIG || {}
	return (
		runtimeConfig.SENTRY_ENABLE_USER_FEEDBACK?.toLowerCase() !== 'false' &&
		(Boolean(runtimeConfig.SENTRY_DSN) || isSentryComponentsEnabled())
	)
}

/**
 * Whether Sentry may send PII (name/email for feedback, etc.) per runtime config.
 */
export function isSentrySendPiiEnabled(): boolean {
	return window.RUNTIME_CONFIG?.SENTRY_SEND_PII?.toLowerCase() === 'true'
}
